import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  InputBase,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import { useSnackbar } from 'notistack';

import { useNavigate } from 'react-router';
import useLocales from '../../../../hooks/useLocales';

// Dialog
import InsufficientBalanceDialog from './InsufficientBalanceDialog';
import ConfirmWithdrawDrawer from './ConfirmWithdrawDrawer';

import conObj from '../../../../utils/connection-assistant';
import { useSelector, useDispatch } from '../../../../redux/store';
import { openTransactionsDialog } from '../../../../redux/slices/transactionsDialog';
import { openAddMemberBankDialog } from '../../../../redux/slices/addMemberBankDialog';
import { fCurrency, fNumberCommaSeparated } from '../../../../utils/formatNumber';
import getPercentage from '../../../../utils/getPercentage';

import palette from '../../../../theme/palette';
import { getUserMemberBalance } from '../../../../redux/slices/lookup';
import { DepositWithdrawSkeleton } from '../deposit/DepositWithdrawSkeleton';
import { ShiningGoldBorder } from '../../../../components/settings/GlobalStyled';
import { usePageVisibility } from '../../../../hooks/usePageVisibility';
import useAuth from '../../../../hooks/useAuth';

const TypographyCustom = styled(Typography)({
  color: palette.transactionDialog.withdraw.text,
  fontSize: '14px',
});

const SuggestedWthdrwBox = styled(Box)(({ theme, isSelected }) => ({
  backgroundColor: palette.transactionDialog.withdraw.suggestedWthdrwBg,
  padding: '11px 4px',
  borderRadius: '6px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  borderWidth: '3px',
  borderStyle: 'solid',
  borderColor: isSelected ? '#E63946' : 'transparent',
}));

const AvailableBalanceTxt = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 500,
  fontFamily: 'sans-serif',
  color: palette.transactionDialog.withdraw.availableBalanceTxt,
}));

const InputLabel = styled(Typography)(({ theme }) => ({
  color: palette.transactionDialog.withdraw.text,
  fontSize: '1rem',
}));

const WithdrawInputBox = styled(Stack)(({ theme }) => ({
  background: palette.transactionDialog.withdraw.withdrawInputBox.bg,
  // border: `1px solid ${palette.transactionDialog.withdraw.withdrawInputBox.border}`,
  // boxShadow: `0 0 0 2px ${palette.transactionDialog.withdraw.withdrawInputBox.shadow}`,
  height: '42px',
  borderRadius: '6px',
  padding: '4px 11px',
  alignItems: 'center',
}));

const WithddrawNote = styled(Box)(({ theme }) => ({
  fontSize: '.75rem',
  color: '#000',
  fontStyle: 'italic',
}));

const StyledSelectOption = styled(Select)(({ theme }) => ({
  width: '100%',
  color: palette.transactionDialog.deposit.select.color,
  fontSize: '14px',
  fontWeight: 600,
  backgroundColor: palette.transactionDialog.deposit.select.bg,
  // border: `1px solid ${palette.transactionDialog.deposit.select.border} !important`,
  borderRadius: '6px',
  '& fieldset': {
    border: 'none',
  },
  '& svg': {
    color: palette.transactionDialog.deposit.select.color,
  },
}));

const NoBankAccount = styled(Typography)(({ theme }) => ({
  fontSize: '.75rem',
  color: palette.transactionDialog.withdraw.noBankAccount,
}));

const WithdrawText = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  color: palette.transactionDialog.withdraw.withdrawText,
  fontWeight: 700,
}));

const ConfirmBtnBox = styled(Grid)(({ theme }) => ({
  padding: '8px 12px',
  position: 'relative',
  backgroundColor: palette.transactionDialog.withdraw.confirmBtnBg,
  borderRadius: '10px',
  marginTop: '10px',
  cursor: 'pointer',
}));

const AddAccountBtn = ({ children }) => (
  <button
    style={{
      fontSize: '.75rem',
      padding: '5px',
      border: 'none',
      borderRadius: '5px',
      background: 'green',
      color: '#000',
      cursor: 'pointer',
      fontWeight: 600,
      marginTop: '10px',
    }}
  >
    {children}
  </button>
);

const Withdraw = () => {
  const { translate } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const isVisible = usePageVisibility();

  const { isAuthenticated } = useAuth();

  const { memberBalance, withdrawRequirement } = useSelector((x) => x.lookup);

  const [withdrawAmount, setWithdrawAmount] = useState('0');

  const [openWitdrawForm, setOpenWithdrawForm] = useState(false);

  const [withdrawInfoSaved, setWithdrawInfoSaved] = useState(false);

  const [confirmWithdraw, setConfirmWithdraw] = useState(false);

  const [openInsufficientBalance, setOpenInsufficientBalance] = useState(false);

  const [memberBankAccounts, setMemberBankAccounts] = useState([]);

  const [selectedBankAccount, setSelectedBankAccount] = useState(null);

  // const [amountInThousandths, setAmountInThousandths] = useState(false);

  const [currencyWithdraw, setCurrencyWithdraw] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [minWithdrawLimit, setMinWithdrawLimit] = useState(0);

  const [maxWithdrawLimit, setMaxWithdrawLimit] = useState(0);

  const [withdrawLimitMessage, setWithdrawLimitMessage] = useState('');

  const [isLoadingMemberBank, setIsLoadingMemberBank] = useState(true);

  useEffect(() => {
    getMemberBankAccounts();
  }, []);

  useEffect(() => {
    if (isVisible && isAuthenticated) getUserMemberBalance();
  }, [isVisible, isAuthenticated]);

  // useEffect(() => {
  //   if (process.env.REACT_APP_CURRENCY_CODE.toUpperCase() === 'MMK' || process.env.REACT_APP_CURRENCY_CODE.toUpperCase() === 'VND') {
  //     setAmountInThousandths(true);
  //   }
  // }, [memberBalance]);

  // useEffect(() => {
  //   const _currencyWithdraw = memberBankAccounts.find((f) => f.id === selectedBankAccount)?.CurrencyWithdraw;
  //   if (_currencyWithdraw) {
  //     setCurrencyWithdraw(Object.values(_currencyWithdraw));
  //   } else {
  //     setCurrencyWithdraw(null);
  //   }
  // }, [selectedBankAccount]);

  const handleConfirm = () => {
    const trimmedWithdrawAmount = typeof withdrawAmount === 'string' ? withdrawAmount.trim() : '';
    if (!trimmedWithdrawAmount) {
      enqueueSnackbar(translate('x_is_required', { x: `${translate('withdraw')} ${translate('amount')}` }), {
        variant: 'error',
        autoHideDuration: 5000,
      });
      return;
    }

    if (Number(memberBalance?.displayBalance) === 0) {
      enqueueSnackbar(`${translate('no_balance')}!`, { variant: 'error', autoHideDuration: 5000 });
      return;
    }

    const _memberBalance = memberBalance?.balance ? parseFloat(memberBalance?.balance.replace(/[^0-9.]/g, '')) : 0;
    // const _memberBalance = amountInThousandths
    // ? parseFloat(memberBalance?.balance.replace(/[^0-9.]/g, '')) * 1000
    // : parseFloat(memberBalance?.balance.replace(/[^0-9.]/g, ''));
    if (parseFloat(trimmedWithdrawAmount) > _memberBalance) {
      enqueueSnackbar(translate('amount_no_exceed_balance'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
      return;
    }

    if (currencyWithdraw) {
      if (parseFloat(trimmedWithdrawAmount.replaceAll(',', '')) < getMinWithdrawAmount()) {
        enqueueSnackbar(translate('minimum_withdraw_amount_is_x', { x: getMinWithdrawAmount() }), {
          variant: 'error',
          autoHideDuration: 5000,
        });
        return;
      }

      if (parseFloat(trimmedWithdrawAmount.replaceAll(',', '')) > getMaxWithdrawAmount()) {
        enqueueSnackbar(translate('maximum_withdraw_amount_is_x', { x: getMaxWithdrawAmount() }), {
          variant: 'error',
          autoHideDuration: 5000,
        });
        return;
      }
    }

    if (!selectedBankAccount) {
      enqueueSnackbar(translate('x_is_required', { x: translate('bank_account') }), {
        variant: 'error',
        autoHideDuration: 5000,
      });
      return;
    }

    setConfirmWithdraw(true);
  };

  const getMinWithdrawAmount = () => {
    const _minimumWithdrawAmount = parseFloat(currencyWithdraw && currencyWithdraw[0].replace(/[^0-9.]/g, ''));
    if (parseFloat(minWithdrawLimit) > 0) {
      return Math.max(parseFloat(minWithdrawLimit), parseFloat(_minimumWithdrawAmount));
    }

    return _minimumWithdrawAmount;
  };

  const getMaxWithdrawAmount = () => {
    const _maximumWithdrawAmount = parseFloat(currencyWithdraw[currencyWithdraw.length - 1].replace(/[^0-9.]/g, ''));
    if (parseFloat(maxWithdrawLimit) > 0) {
      return Math.min(parseFloat(maxWithdrawLimit), parseFloat(_maximumWithdrawAmount));
    }

    return _maximumWithdrawAmount;
  };

  const getMemberBankAccounts = () => {
    setIsLoadingMemberBank(true);

    conObj
      .post('members/banks.json')
      .then((res) => {
        if (res.status === 200) {
          if (res.data?.data?.status === 1) {
            setMemberBankAccounts(res.data?.data?.data);
            // set selected
            setSelectedBankAccount(res.data?.data?.data[0]?.id);

            // set suggested currency withdraw amount
            setCurrencyWithdraw(Object.values(res.data?.data?.currencyWithdraw));

            setMinWithdrawLimit(res.data?.data?.minWithdrawLimit);
            setMaxWithdrawLimit(res.data?.data?.maxWithdrawLimit);

            setWithdrawLimitMessage(res.data?.data?.withdrawLimitMessage);
            setConfirmWithdraw(false);
          } else {
            //
          }
        }
      })
      .catch((err) => {
        console.log(`Server Error: ${err.message}`);
      })
      .finally(() => setIsLoadingMemberBank(false));
  };

  const submitWithdraw = () => {
    const requestData = {
      'transaction-member_bank_id': selectedBankAccount,
      'transaction-amount': parseFloat(withdrawAmount.toString().replaceAll(',', '')),
      // 'transaction-amount': amountInThousandths
      // ? parseFloat(withdrawAmount.trim()) / 1000
      // : parseFloat(withdrawAmount.trim()),
      'transaction-note': '',
    };

    setIsSubmitting(true);

    conObj
      .post('transactions/withdraws.json', requestData)
      .then((res) => {
        if (res.status === 200) {
          if (res.data?.data?.status === 1) {
            enqueueSnackbar(res?.data?.data?.msg, { variant: 'success', autoHideDuration: 5000 });
            dispatch(openTransactionsDialog({ open: false, isDeposit: true }));
            getUserMemberBalance();
          } else if (res.data?.data?.data?.validationErrors) {
            enqueueSnackbar(res?.data?.data?.validationErrors.toString(), {
              variant: 'error',
              autoHideDuration: 5000,
            });
          }
        }
      })
      .catch((err) => {
        console.log(`Server Error: ${err.message}`);
        enqueueSnackbar(err.message, { variant: 'error', autoHideDuration: 5000 });
        dispatch(openTransactionsDialog({ open: false, isDeposit: true }));
      })
      .finally(() => setIsSubmitting(false));
  };

  const toggleDrawer = (open) => {
    setConfirmWithdraw(open);
  };

  const makeCurrencyWithdraw = (amount) => {
    const _amount = amount;
    // const _amount = amountInThousandths ? amount * 1000 : amount;
    return (
      <Grid item xl={4} lg={4} md={4} sm={4} xs={4}>
        <SuggestedWthdrwBox
          onClick={() => setWithdrawAmount(_amount.toString())}
          sx={{
            borderColor: withdrawAmount === _amount ? '#fff4ba' : 'transparent',
          }}
        >
          <TypographyCustom sx={{ fontWeight: 700, wordBreak: 'break-all' }}>{`${fCurrency(
            _amount
          )}`}</TypographyCustom>
          {withdrawAmount === _amount && (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                position: 'absolute',
                bottom: 0,
                color: '#0d1a8c',
                height: '16px',
                right: 0,
                width: '17px',
                zIndex: 1,
              }}
            >
              <path d="M17 0L0 16H12C14.7614 16 17 13.7614 17 11V0Z" fill="currentColor" />
              <path
                d="M9.35493 11.6942L11.1083 12.8822L13.6758 9.00007"
                stroke="white"
                strokeWidth="0.822792"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </SuggestedWthdrwBox>
      </Grid>
    );
  };

  const getPlaceholder = (minWithdrawLimit, maxWithdrawLimit) => {
    if (minWithdrawLimit > 0 && maxWithdrawLimit > 0) {
      return `${translate('minimum')} ${minWithdrawLimit} ~ MAX ${maxWithdrawLimit}`;
    }
    if (minWithdrawLimit === 0 && maxWithdrawLimit === 0) {
      return `${translate('enter_amount')}`;
    }
    if (minWithdrawLimit > 0) {
      return `${translate('minimum')} ${minWithdrawLimit}`;
    }
    return `${translate('maximum')} ${maxWithdrawLimit}`;
  };

  if (isLoadingMemberBank) return <DepositWithdrawSkeleton />;

  return (
    <Box>
      <Stack direction="row" alignItems={'center'}>
        <AvailableBalanceTxt>{translate('available_balance')}</AvailableBalanceTxt>
        {/* <Iconify icon={'ph:question'} width={20} height={20} sx={{ color: 'rgba(255, 255, 255, 0.85)', ml: '10px' }} /> */}
      </Stack>

      {/* Balance */}
      <TypographyCustom>{`${memberBalance?.currencyCode} ${
        memberBalance?.displayBalance ? fCurrency(memberBalance?.displayBalance) : 0
      }`}</TypographyCustom>

      {(withdrawRequirement?.calculation_type === 'turnover' || withdrawRequirement?.calculation_type === 'winover') &&
        withdrawRequirement?.requirement !== 0 && (
          <Stack
            direction="column"
            sx={{
              p: 1,
              border: '1px solid #f2c556',
              borderRadius: '10px',
              mt: 2,
              background: palette.transactionDialog.deposit.selectedBankBox.bg,
            }}
          >
            <LinearProgress
              variant="determinate"
              value={getPercentage(withdrawRequirement?.current, withdrawRequirement?.requirement)}
            />
            <Typography sx={{ fontSize: '.75rem', color: '#000' }}>
              {`${
                withdrawRequirement?.calculation_type === 'turnover' ? translate('turnover') : translate('balance')
              } ${translate('requirement')}`}
              :{' '}
            </Typography>
            <Typography sx={{ fontSize: '.75rem', color: '#000', lineHeight: 1 }}>
              {fCurrency(withdrawRequirement?.current)} / {fCurrency(withdrawRequirement?.requirement)}
            </Typography>
          </Stack>
        )}

      {/* Amount */}
      <Box sx={{ mb: '10px', my: 2 }}>
        <Stack direction="row" alignItems={'center'}>
          <InputLabel>
            <span style={{ color: 'red', marginRight: 5 }}>*</span>
            {translate('amount')}
          </InputLabel>
          {/* <Iconify icon={'uiw:information-o'} width={15} height={15} sx={{ color: 'rgb(95, 95, 95)', ml: '5px' }} /> */}
        </Stack>

        <ShiningGoldBorder>
          <WithdrawInputBox direction="row" spacing={1} flex={1}>
            <TypographyCustom sx={{ mr: '4px', fontWeight: 700, color: '#000', fontSize: '20px' }}>
              {memberBalance?.currencyCode}
            </TypographyCustom>

            <Stack flex={1}>
              <InputBase
                name="withdrawAmount"
                value={fNumberCommaSeparated(withdrawAmount) !== '0' ? fNumberCommaSeparated(withdrawAmount) : ''}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder={currencyWithdraw && getPlaceholder(getMinWithdrawAmount(), getMaxWithdrawAmount())}
                sx={{
                  color: '#000',
                  fontWeight: 700,
                  fontSize: '20px',
                  input: {
                    '&::placeholder': {
                      color: '#fff',
                      opacity: 0.3,
                    },
                  },
                }}
              />
            </Stack>
          </WithdrawInputBox>
        </ShiningGoldBorder>
        {withdrawLimitMessage && <WithddrawNote dangerouslySetInnerHTML={{ __html: withdrawLimitMessage }} />}
      </Box>

      {memberBankAccounts.length > 0 ? (
        <Box sx={{ mb: '10px' }}>
          <Stack direction="row" alignItems={'center'}>
            <InputLabel>
              <span style={{ color: 'red', marginRight: 5 }}>*</span>
              {translate('bank_account')}
            </InputLabel>
          </Stack>

          <ShiningGoldBorder>
            <StyledSelectOption
              value={selectedBankAccount}
              onChange={(e) => setSelectedBankAccount(e.target.value)}
              disableUnderline={true}
            >
              {memberBankAccounts?.map((item, index) => (
                <MenuItem key={index} value={item?.id}>
                  {`${item?.account_number} - ${item?.Bank?.name}`}
                </MenuItem>
              ))}
            </StyledSelectOption>
          </ShiningGoldBorder>
          {/* {errors?.bank && <span style={{ fontSize: '.75rem', color: 'red' }}>{errors?.bank}</span>} */}
        </Box>
      ) : (
        <NoBankAccount>{translate('no_bank_account')}</NoBankAccount>
      )}
      <Button
        onClick={() => dispatch(openAddMemberBankDialog({ open: true, onClose: getMemberBankAccounts }))}
        color={'primary'}
        variant="contained"
        sx={{
          fontSize: '.75rem',
          padding: '3px 5px',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
          fontWeight: 600,
          marginTop: '10px',
        }}
      >
        {translate('add_account')}
      </Button>
      {/* <AddAccountBtn onClick={() => dispatch(openAddMemberBankDialog({ open: true, onClose: getMemberBankAccounts }))}>
        {translate('add_account')}
      </AddAccountBtn> */}

      {currencyWithdraw && (
        <Box sx={{ mt: '10px' }}>
          <Grid container spacing={'20px'}>
            {currencyWithdraw.map((item) => makeCurrencyWithdraw(item))}
          </Grid>
        </Box>
      )}

      <ConfirmBtnBox
        container
        sx={{
          opacity: withdrawRequirement?.allow_withdraw === false ? 0.1 : 1,
        }}
        onClick={() => (withdrawRequirement?.allow_withdraw === false ? {} : handleConfirm())}
      >
        <Grid
          item
          xl={6}
          lg={6}
          md={6}
          sm={6}
          xs={12}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { sm: 'unset', xs: 'center' },
          }}
        >
          <WithdrawText>{translate('withdraw')}</WithdrawText>
        </Grid>
        <Grid
          item
          xl={6}
          lg={6}
          md={6}
          sm={6}
          xs={12}
          sx={{ display: 'flex', justifyContent: { sm: 'end', xs: 'center' } }}
        >
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Typography sx={{ fontSize: '26px', color: '#fff', fontWeight: 700, mr: 2, wordBreak: 'break-all' }}>{`${
              memberBalance?.currencyCode
            } ${fCurrency(withdrawAmount)}`}</Typography>
            <svg
              viewBox="64 64 896 896"
              focusable="false"
              data-icon="right-circle"
              width="1em"
              height="1em"
              fill="currentColor"
              aria-hidden="true"
              style={{ color: '#fff' }}
            >
              <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm154.7 454.5l-246 178c-5.3 3.8-12.7 0-12.7-6.5v-46.9c0-10.2 4.9-19.9 13.2-25.9L566.6 512 421.2 406.8c-8.3-6-13.2-15.6-13.2-25.9V334c0-6.5 7.4-10.3 12.7-6.5l246 178c4.4 3.2 4.4 9.8 0 13z" />
            </svg>
          </Stack>
        </Grid>
      </ConfirmBtnBox>

      {openInsufficientBalance && (
        <InsufficientBalanceDialog
          openInsufficientBalance={openInsufficientBalance}
          setOpenInsufficientBalance={setOpenInsufficientBalance}
        />
      )}
      {confirmWithdraw && (
        <ConfirmWithdrawDrawer
          confirmWithdraw={confirmWithdraw}
          withdrawAmount={fCurrency(withdrawAmount)}
          toggleDrawer={toggleDrawer}
          submitWithdraw={submitWithdraw}
          isSubmitting={isSubmitting}
        />
      )}
    </Box>
  );
};

export default Withdraw;
